import { Button } from "@/components/ui/baseUi/button"
import { AssignedCoachInput } from "@/components/ui/forms/assignedCoachInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Text } from "@/components/ui/baseUi/text"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@/modules/boxSlot/boxSlotTypes"
import type { BoxSlotTemplateGroupFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateGroupFormikEntity"
import { cn } from "@/modules/lib/utils"
import { type SimpleFormik } from "@/typings/formik"
import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { DropInIcon, DropInSelectedIcon } from "@images/svgIcons/slots/drop-in"
import { MultiSlotsIcon, MultiSlotsSelectedIcon } from "@images/svgIcons/slots/multi-slots"
import { TrialSessionIcon, TrialSessionSelectedIcon } from "@images/svgIcons/slots/trial-session"
import { type BoxSlotTemplateGroupInputKey } from "@modules/boxSlotTemplate/boxSlotTemplateGroupFormikTypes"
import {
  getCancellationBeginningOptions,
  getRegistrationBeginningOptions,
  getRegistrationEndOptions,
  getSlotDurationOptions,
} from "@modules/boxSlotTemplate/boxSlotTemplateUtils"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUTimeDropdownOrCustomizedTimeInput } from "@modules/ui/components/huDropdownWithCustomizedTimeInput"
import type { FieldInputProps } from "formik"
import { ArrowRightIcon } from "lucide-react"

export function BoxSlotTemplateGroupSettingsForm({
  formik,
  moveToScheduleStep,
  className,
  ...props
}: {
  formik: SimpleFormik<BoxSlotTemplateGroupFormikEntity, BoxSlotTemplateGroupInputKey>
  moveToScheduleStep: () => void
} & React.ComponentProps<"div">) {
  const { hideSidebar } = useSidebar()

  const durationOptions = getSlotDurationOptions()
  const registrationBeginningOptions = getRegistrationBeginningOptions()
  const registrationEndOptions = getRegistrationEndOptions()
  const cancellationBeginningOptions = getCancellationBeginningOptions()

  const toggleMultiBooking = () => {
    void formik.setFieldValue("allowMultiBooking", !formik.values.allowMultiBooking)
  }

  const toggleDropIn = () => {
    void formik.setFieldValue("dropinsAccepted", !formik.values.dropinsAccepted)
  }

  const toggleTrialSessions = () => {
    void formik.setFieldValue("trialsAccepted", !formik.values.trialsAccepted)
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <div className="flex flex-col gap-2">
        <Text fontStyle="TM">{word("box.slotTemplateGroup.form.settings")}</Text>
        <HUCardAlert
          title={""}
          description={word("box.slotTemplateGroup.form.defaultValuesExplanation")}
          icon={
            <InformationAlertIcon
              className="text-primary-600"
              color="currentColor"
              backgroundColor="var(--primary-100)"
            />
          }
        />
      </div>

      <HUTimeDropdownOrCustomizedTimeInput
        name="slotDuration"
        label={word("box.slotTemplateGroup.form.slotDuration.label")}
        dropdownOptions={durationOptions}
        getFieldProps={() => formik.getFieldProps("slotDuration") as FieldInputProps<number>}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage(`slotDuration`, formik)}
        isRequiredInput
      />

      <StandardInput
        isRequiredInput
        label={word("programmation.slotTemplates.form.participantLimit.label")}
        getFieldProps={formik.getFieldProps}
        name="participantLimit"
        error={getFormErrorMessage("participantLimit", formik, { errorMessage: word("global.form.required") })}
        setFieldValue={formik.setFieldValue}
        inputProps={{
          type: "number",
          inputMode: "numeric",
          min: BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
          max: BOX_SLOT_PARTICIPANTS_LIMIT_MAX,
          step: 1,
          onChange: (e) => {
            const value = parseInt(e.target.value)
            if (value > BOX_SLOT_PARTICIPANTS_LIMIT_MAX) {
              void formik.setFieldValue("participantLimit", BOX_SLOT_PARTICIPANTS_LIMIT_MAX)
            } else if (value < BOX_SLOT_PARTICIPANTS_LIMIT_MIN) {
              void formik.setFieldValue("participantLimit", BOX_SLOT_PARTICIPANTS_LIMIT_MIN)
            } else {
              void formik.setFieldValue("participantLimit", value)
            }
          },
          onFocus: (e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false }),
        }}
      />

      <div className="flex flex-row items-center justify-between gap-2">
        {/* Drop in accepted */}
        <div
          className={cn(
            "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
            formik.values.dropinsAccepted
              ? "border border-primary-500"
              : "border border-borderDefault hover:border-borderDefault-hover",
          )}
          onClick={toggleDropIn}
        >
          {formik.values.dropinsAccepted ? <DropInSelectedIcon color="" /> : <DropInIcon color="" />}
          <Text
            fontStyle="TS"
            className={`text-center ${formik.values.dropinsAccepted ? "text-textDefault-body" : "text-textDisabled"}`}
          >
            {formik.values.dropinsAccepted
              ? word("box.slotTemplateGroup.form.dropIn.allowed")
              : word("box.slotTemplateGroup.form.dropIn.rejected")}
          </Text>
        </div>

        {/* Trial session accepted*/}
        <div
          className={cn(
            "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
            formik.values.trialsAccepted
              ? "border border-primary-500"
              : "border border-borderDefault hover:border-borderDefault-hover",
          )}
          onClick={toggleTrialSessions}
        >
          {formik.values.trialsAccepted ? <TrialSessionSelectedIcon color="" /> : <TrialSessionIcon color="" />}
          <Text
            fontStyle="TS"
            className={`text-center ${formik.values.trialsAccepted ? "text-textDefault-body" : "text-textDisabled"}`}
          >
            {formik.values.trialsAccepted
              ? word("box.slotTemplateGroup.form.trialSession.allowed")
              : word("box.slotTemplateGroup.form.trialSession.rejected")}
          </Text>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" className="uppercase">
          {word("box.slotTemplateGroup.form.registration.label")}
        </Text>
        <Text fontStyle="BS">{word("box.slotTemplateGroup.form.registration.description")}</Text>
        <div className="flex gap-2">
          <HUTimeDropdownOrCustomizedTimeInput
            name="limitStart"
            dropdownOptions={registrationBeginningOptions}
            getFieldProps={() => formik.getFieldProps("limitStart") as FieldInputProps<number>}
            label={word("box.slotTemplateGroup.form.registration.beginning")}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage(`limitStart`, formik)}
          />
          <HUTimeDropdownOrCustomizedTimeInput
            name="limitEnd"
            dropdownOptions={registrationEndOptions}
            label={word("box.slotTemplateGroup.form.registration.end")}
            getFieldProps={() => formik.getFieldProps("limitEnd") as FieldInputProps<number>}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage(`limitEnd`, formik)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" className="uppercase">
          {word("box.slotTemplateGroup.form.cancellation.label")}
          <Text fontStyle="LL" className="text-red-500">
            {" *"}
          </Text>
        </Text>
        <Text fontStyle="BS">{word("box.slotTemplateGroup.form.cancellation.description")}</Text>
        <HUTimeDropdownOrCustomizedTimeInput
          name="limitCancel"
          dropdownOptions={cancellationBeginningOptions}
          label={word("box.slotTemplateGroup.form.cancellation.beginning")}
          getFieldProps={() => formik.getFieldProps("limitCancel") as FieldInputProps<number>}
          setFieldValue={formik.setFieldValue}
          error={getFormErrorMessage(`limitCancel`, formik)}
        />
      </div>

      <div
        className={cn(
          "flex w-full cursor-pointer items-center gap-4 rounded-2xl border-solid p-6 transition-all duration-100",
          formik.values.allowMultiBooking
            ? "border border-primary-500"
            : "border border-borderDefault hover:border-borderDefault-hover",
        )}
        onClick={toggleMultiBooking}
      >
        <div className="flex w-[45%] flex-col items-center gap-3">
          {formik.values.allowMultiBooking ? <MultiSlotsSelectedIcon color="" /> : <MultiSlotsIcon color="" />}
          <Text
            fontStyle="TS"
            className={`text-center ${formik.values.allowMultiBooking ? "text-textDefault-body" : "text-textDisabled"}`}
          >
            {formik.values.allowMultiBooking
              ? word("box.slotTemplateGroup.form.mutliSlotBooking.allowed")
              : word("box.slotTemplateGroup.form.mutliSlotBooking.rejected")}
          </Text>
        </div>
        <Text fontStyle="BS" className="pre-line w-[55%] text-textDefault-caption">
          {word("box.slotTemplateGroup.form.mutliSlotBooking.description")}
        </Text>
      </div>

      <StandardInput
        label={word("box.slotTemplateGroup.form.area.title")}
        description={word("box.slotTemplateGroup.form.area.subtitle")}
        name="location"
        isRequiredInput={true}
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("location", formik)}
        inputProps={{
          placeholder: word("box.slotTemplateGroup.form.area.placeholder"),
        }}
      />

      <div className="flex flex-col">
        <AssignedCoachInput
          label={word("box.slotTemplateGroup.form.assignedStaffMembers.label")}
          textDescription={word("box.slotTemplateGroup.form.assignedStaffMembers.description")}
          name="assignedStaffMembers"
          isRequiredInput={false}
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("assignedStaffMembers", formik)}
        />
      </div>

      <div className="mt-3 flex justify-end pb-4">
        <div className="flex grow gap-3 sm:flex-row">
          <Button variant="outline" size="lg" className="flex-1" onClick={() => hideSidebar()}>
            {word("global.cancel")}
          </Button>
          <Button variant="primary" size="lg" className="mr-2 flex-1" onClick={moveToScheduleStep}>
            {word("global.next")}
            <ArrowRightIcon color="currentColor" />
          </Button>
        </div>
      </div>
    </div>
  )
}
